import React, { useEffect, useState } from "react";
import classes from "./NavBar.module.scss";
import useTheme from "../../utils/useTheme";
import useTranslation from "../../utils/i18n/useTranslation";
import { useLocation, useNavigate } from "react-router-dom";
import { MdMenu, MdClose } from "react-icons/md";
import { RiShieldKeyholeLine } from "react-icons/ri";
import { getVisibilitySettings, DEFAULT_VISIBILITY } from "../../utils/network/Crud";

const NavBar = () => {
  const navigate = useNavigate();
  const { userData, updateUserData, currentYear, setCurrentYear, isLanguageEnglish, toggleLangauge } =
    useTheme();
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState(false);
  const [visibility, setVisibility] = useState(DEFAULT_VISIBILITY);
  const [visibilityLoaded, setVisibilityLoaded] = useState(false);

  useEffect(() => {
    getVisibilitySettings()
      .then((data) => {
        setVisibility(data);
      })
      .catch(() => {
        // Fail open: an unexpected error shouldn't leave every nav link hidden forever.
        setVisibility(DEFAULT_VISIBILITY);
      })
      .finally(() => {
        setVisibilityLoaded(true);
      });
  }, []);

  const allTabs = [
    { key: "/", title: t("navHome"), show: true },
    { key: "donations", title: t("navDonation"), show: visibility.showDonations },
    { key: "expenses", title: t("navExpense"), show: visibility.showExpenses },
    { key: "events", title: t("navEvents"), show: visibility.showEvents },
    { key: "gallery", title: t("navGallery"), show: visibility.showGallery },
    { key: "community", title: t("navCommunity"), show: visibility.showCommunity },
  ];
  const tabs = allTabs.filter((tab) => tab.key === "/" ? visibilityLoaded : visibilityLoaded && tab.show);

  const FOUNDING_YEAR = 2024;
  const thisYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = thisYear; year >= FOUNDING_YEAR; year--) {
    yearOptions.push(year);
  }

  const location = useLocation();
  const activeTab =
    location.pathname === "/" ? "/" : location.pathname.slice(1);

  const handleNavigateToPage = (id) => {
    navigate(id);
    setOpenMenu(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    updateUserData({});
    navigate("/");
  };

  return (
    <nav className={classes.nav}>
      <div className={classes.inner}>
        <div className={classes.brand} onClick={() => handleNavigateToPage("/")}>
          <img
            className={classes.logo}
            src={require("../../assets/logo.jpg")}
            alt="Samiti logo"
          />
          <span className={classes.brandText}>Navdurga Utsav Samiti</span>
        </div>

        <ul className={classes.links}>
          {tabs?.map((elem, index) => (
            <li key={index}>
              <button
                className={activeTab === elem?.key ? classes.activeLink : classes.link}
                onClick={() => handleNavigateToPage(elem?.key)}
              >
                {elem?.title}
              </button>
            </li>
          ))}
        </ul>

        <div className={classes.actions}>
          <select
            className={classes.yearSelect}
            value={currentYear}
            onChange={(e) => setCurrentYear(Number(e.target.value))}
            aria-label="Select year"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <button
            className={classes.langBtn}
            onClick={toggleLangauge}
            aria-label="Toggle language"
            title="Toggle language"
          >
            {isLanguageEnglish ? "हि" : "EN"}
          </button>

          <button
            className={classes.authBtn}
            onClick={() =>
              userData?.isLoggedIn ? handleLogout() : handleNavigateToPage("login")
            }
          >
            {userData?.isLoggedIn ? t("logout") : t("login")}
          </button>

          <RiShieldKeyholeLine
            className={classes.adminIcon}
            title="Admin Dashboard"
            onClick={() => navigate("/dashboard")}
          />

          <button
            className={classes.menuBtn}
            aria-label={openMenu ? "Close menu" : "Open menu"}
            onClick={() => setOpenMenu(!openMenu)}
          >
            {openMenu ? <MdClose /> : <MdMenu />}
          </button>
        </div>
      </div>

      <div className={`${classes.mobileMenu} ${openMenu ? classes.mobileMenuOpen : ""}`}>
        <ul>
          {tabs?.map((elem, index) => (
            <li key={index}>
              <button
                className={activeTab === elem?.key ? classes.activeLink : classes.link}
                onClick={() => handleNavigateToPage(elem?.key)}
              >
                {elem?.title}
              </button>
            </li>
          ))}
        </ul>
        <div className={classes.mobileYear}>
          <span>{t("year")}</span>
          <select
            value={currentYear}
            onChange={(e) => setCurrentYear(Number(e.target.value))}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <button className={classes.mobileLangBtn} onClick={toggleLangauge}>
          {isLanguageEnglish ? "हिन्दी में देखें" : "View in English"}
        </button>
        <button
          className={classes.mobileAuthBtn}
          onClick={() =>
            userData?.isLoggedIn ? handleLogout() : handleNavigateToPage("login")
          }
        >
          {userData?.isLoggedIn ? t("logout") : t("login")}
        </button>
      </div>
    </nav>
  );
};

export default NavBar;
